import { useRequest } from 'ahooks';
import { Badge, Tabs, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { getDepartmentList } from 'services/api/dashboard';
import { LoadingOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import Negative from './Negative';
import UserVoice from './UserVoice';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { useTranslate } from '@tolgee/react';
import Buyer from './Buyer';
import { useAuthorization } from 'components/Authorization';
import Cache from 'utils/Cache';
import { getHighRiskBuyers } from './service';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

function ReviewAnalysis() {
  const { t } = useTranslate();

  const { whiteListConfig: { REVIEW_ANALYSIS, HIGH_RISK_BUYER } = {} } = useOrgConfigStateValue();

  const onlyHighRiskBuyer = HIGH_RISK_BUYER && !REVIEW_ANALYSIS;

  const [activeKey, setActiveKey] = useState(onlyHighRiskBuyer ? '恶意买家监测' : '差评分析');
  useEffect(() => {
    if (onlyHighRiskBuyer) {
      setActiveKey('恶意买家监测');
    }
  }, [onlyHighRiskBuyer]);

  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const ref = useRef(new Cache(localStorage, `LastHighRiskTime/${payload?.id}`));

  const [lastHighRiskTime, setLastHighRiskTime] = useState(() => ref.current.get<string>());

  const highRiskState = useRequest(() => {
    return getHighRiskBuyers({
      pageNum: 1,
      pageSize: 1,
    }).then((res) => {
      return res.buyers[0];
    });
  });

  const { loading: departmentListLoading, data: { departments } = {} } = useRequest(() =>
    getDepartmentList('order'),
  );

  if (!departments) {
    return departmentListLoading ? <LoadingOutlined /> : <></>;
  }

  const isBuyer = activeKey === '恶意买家监测';

  return (
    <Container isBuyer={String(isBuyer)}>
      <header>
        <Tabs activeKey={activeKey} onChange={setActiveKey}>
          {!onlyHighRiskBuyer && [
            <Tabs.TabPane
              key="差评分析"
              tab={
                <div>
                  {t('差评分析')}
                  <Tooltip title={t('4星以下评价为差评')}>
                    <What />
                  </Tooltip>
                </div>
              }
            />,
            <Tabs.TabPane key="用户之声" tab={t('用户之声')} />,
          ]}
          <Tabs.TabPane
            key="恶意买家监测"
            tab={
              <Badge
                dot={Number(highRiskState.data?.createTime || 0) > Number(lastHighRiskTime || 0)}
              >
                {t('恶意买家监测')}
              </Badge>
            }
          />
        </Tabs>
      </header>
      {!onlyHighRiskBuyer && [
        <Negative visible={activeKey === '差评分析'} departments={departments} />,
        <UserVoice visible={activeKey === '用户之声'} departments={departments} />,
      ]}
      <Buyer
        visible={isBuyer}
        onLastTimeChange={(time) => {
          setLastHighRiskTime(time);
          ref.current.set(time);
        }}
      />
    </Container>
  );
}

const Container = styled.div<{ isBuyer: string }>`
  ${({ isBuyer }) =>
    isBuyer === 'true'
      ? `
    display: flex;
    flex-direction: column;
    height: 100%;
  `
      : ''}
  .ant-tabs {
    .ant-tabs-nav {
      padding: 0 16px;
      margin-bottom: 0;
      background: #fff;
    }
  }
  > header {
    flex: 0 0 auto;
    position: sticky;
    top: 0;
    margin-top: 16px;
    z-index: 3;
  }
  > div {
    > header:first-of-type {
      position: sticky;
      top: 46px;
      z-index: 3;
    }
    > * {
      margin-bottom: 16px;
    }
  }
`;

export default ReviewAnalysis;
